import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { affiliateService } from '../../services/affiliateService';

/**
 * Referral link landing: bettoradvantage.com/r/:code
 *
 * Invite-only program: the code is validated with the same
 * granted-only check signup uses (affiliateService.validateReferralCode).
 * A valid code is stored in localStorage under 'referralCode' — the key
 * LoginScreen already reads on mount to prefill the signup form — a
 * click is logged fire-and-forget, and the visitor lands on signup.
 * Invalid or revoked codes store nothing and just go home.
 */
const ReferralRedirectScreen = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const handle = async () => {
      // Signed-in users can't be re-attributed; just send them home.
      if (currentUser) {
        navigate('/', { replace: true });
        return;
      }

      try {
        const result = await affiliateService.validateReferralCode(code || '');

        if (result.isValid) {
          try {
            localStorage.setItem('referralCode', result.referrer.affiliateCode);
          } catch (storageError) {
            // Storage unavailable (private mode etc.) — the click still
            // counts, and the code can be typed into the signup form.
            console.warn('Could not store referral code:', storageError);
          }

          // Fire-and-forget click log; never block the redirect on it.
          try {
            fetch('/.netlify/functions/affiliate-click', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: result.referrer.affiliateCode }),
              keepalive: true
            }).catch(() => {});
          } catch (clickError) {
            // Non-fatal by design.
          }

          navigate('/login?signup=true', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Error handling referral link:', error);
      }

      navigate('/', { replace: true });
    };

    handle();
    // AuthProvider renders children only after auth state has loaded,
    // so currentUser is settled on mount; handledRef guards re-runs.
  }, [code, navigate, currentUser]);

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Taking you to Bettor Advantage…
      </Typography>
    </Box>
  );
};

export default ReferralRedirectScreen;
